import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo, aboutFaqs } from '@/mocks/aboutMocks';
import { useState } from 'react';

const contactFormUrl = 'https://readdy.ai/api/form/d9pmvmhn2trhb35gvi7g';

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const res = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as URLSearchParams).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  if (formStatus === 'success') {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
          <i className="ri-check-line text-2xl text-accent-600"></i>
        </div>
        <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">提交成功</h3>
        <p className="text-sm text-foreground-500">感谢您的留言，我们将在1-2个工作日内回复您。</p>
        <button
          onClick={() => setFormStatus('idle')}
          className="mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          发送新消息
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form="">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
            <input
              type="text"
              name="name"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
            <input
              type="email"
              name="email"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
              placeholder="请输入您的邮箱"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题 *</label>
          <input
            type="text"
            name="subject"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
            placeholder="请输入消息主题"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            留言内容 * <span className="text-foreground-400 font-normal">({charCount}/500)</span>
          </label>
          <textarea
            name="message"
            required
            rows={5}
            maxLength={500}
            onChange={(e) => setCharCount(e.target.value.length)}
            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors resize-none"
            placeholder="请输入您的留言内容..."
          />
        </div>
        <div className="anti-spam-field">
          <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
        </div>
        {formStatus === 'error' && (
          <p className="text-sm text-red-500">{formError}</p>
        )}
        <button
          type="submit"
          className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          发送消息
        </button>
      </div>
    </form>
  );
}

export default function AboutPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于 aigei',
    description: 'aigei 品牌介绍、发展历程、团队信息和联系方式',
    url: 'https://www.aigei.com/about',
    about: {
      '@type': 'Organization',
      name: 'aigei',
      description: aboutInfo.mission,
      foundingDate: aboutInfo.foundingYear,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: aboutInfo.contact.phone,
        email: aboutInfo.contact.email,
        contactType: 'customer service',
      },
    },
  };

  const aboutFaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="aigei 是国内领先的游戏素材、音效、音乐及视觉资源免费下载平台。了解aigei品牌故事、发展历程、团队信息及联系方式。"
        keywords="aigei,关于aigei,aigei团队,aigei联系方式,游戏素材平台"
        canonicalUrl="/about"
        schema={aboutSchema}
      />

      <main>
        <div className="relative h-[280px] md:h-[360px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20creative%20studio%20workspace%20with%20team%20collaboration%20atmosphere%2C%20modern%20office%20with%20natural%20light%2C%20design%20studio%20vibe%2C%20inspirational%20creative%20environment&width=1600&height=500&seq=about-banner&orientation=landscape"
            alt="关于aigei"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-4 md:px-6 pb-8 md:pb-10">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">关于 aigei</h1>
              <p className="mt-2 text-sm md:text-base text-white/80">{aboutInfo.brandSlogan}</p>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-3">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]} />
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
              <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                {aboutInfo.story}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-14">
              {aboutInfo.stats.map((stat, i) => (
                <div key={i} className="text-center p-5 bg-background-100 rounded-xl">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</p>
                  <p className="mt-1 text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 text-center">品牌使命</h2>
              <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto text-center">
                {aboutInfo.mission}
              </p>
            </div>

            <div className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 transform md:-translate-x-px"></div>
                <div className="space-y-8">
                  {aboutInfo.milestones.map((m, i) => (
                    <div key={i} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                        <div className="hidden md:block">
                          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-2">{m.year}</span>
                          <p className="text-foreground-600 text-sm">{m.event}</p>
                        </div>
                        <div className="md:hidden">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-1">{m.year}</span>
                          <p className="text-foreground-600 text-sm">{m.event}</p>
                        </div>
                      </div>
                      <div className="absolute left-2 md:left-1/2 w-5 h-5 rounded-full bg-primary-500 border-4 border-background-50 transform md:-translate-x-1/2 mt-1.5"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">核心团队</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {aboutInfo.team.map((member, i) => (
                <div key={i} className="text-center p-5 bg-background-50 rounded-xl">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover mx-auto mb-3"
                    loading="lazy"
                  />
                  <h3 className="font-heading text-sm font-bold text-foreground-950">{member.name}</h3>
                  <p className="text-xs text-primary-500 mb-1">{member.role}</p>
                  <p className="text-xs text-foreground-500">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-6">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.author}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <span className="text-sm text-foreground-700 font-medium">{t.author}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-950">邮箱</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-950">电话</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-950">地址</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-950">工作时间</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">发送消息</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <script type="application/ld+json">{JSON.stringify(aboutFaqSchema)}</script>
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">常见问题</h2>
            <div className="space-y-3">
              {aboutFaqs.map((faq, index) => (
                <div key={index} className="bg-background-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-200/70 transition-colors"
                  >
                    <span className="font-heading text-base font-bold text-foreground-950 pr-4">{faq.question}</span>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${
                      openFaqIndex === index ? 'bg-primary-100 text-primary-500' : 'bg-background-200 text-foreground-400'
                    }`}>
                      <i className={`${openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'} text-sm`}></i>
                    </div>
                  </button>
                  <div className={`transition-all duration-300 overflow-hidden ${
                    openFaqIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}